import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  createElement,
} from 'react';
import { StyleSheet, type ViewStyle } from 'react-native';
import { isNotEmpty } from '../../utils';

interface WebViewProps {
  title?: string;
  source: {
    uri?: string;
    html?: string;
    method?: string;
    body?: string;
    headers?: Record<string, string>;
  };
  width?: number;
  height?: number;
  onLoad?: () => void;
  onMessage?: (params: {
    nativeEvent: { data?: string };
    /**
     * [web 전용] 메시지를 보낸 iframe 의 contentWindow. 결과를 되돌려줘야 하는 양방향
     * 브릿지에서 응답 대상으로 쓴다. 네이티브 WebView 에는 없는 필드다.
     */
    source?: Window;
    /** [web 전용] 그 iframe 의 오리진. 응답 postMessage 의 targetOrigin 으로 쓴다. */
    origin?: string;
  }) => void;
  onTitleChange?: (title: string) => void;
  scrollEnabled?: boolean;
  injectedJavaScript?: string;
  style?: ViewStyle;
  cookies?: Array<{
    name: string;
    value?: string | null;
    domain?: string;
    path?: string;
  }>;
}

export const WebView: React.FC<WebViewProps> = ({
  title,
  source,
  onLoad,
  onMessage,
  onTitleChange,
  scrollEnabled = true,
  injectedJavaScript,
  style,
  width,
  height,
  cookies,
}) => {
  const [html, setHtml] = useState<string | null>(null);
  // fetch(srcDoc) 경로가 실패했는지. 실패를 방치하면 srcDoc 이 undefined 로 남아
  // iframe 이 about:blank 로 비어버리므로, src 직접 로드로 폴백하는 데 쓴다.
  const [fetchFailed, setFetchFailed] = useState(false);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const titleObserverRef = useRef<MutationObserver | null>(null);

  // Cookie 설정을 위한 JavaScript 생성
  const getCookieInjectionScript = useCallback(() => {
    if (!cookies || cookies.length === 0) return '';

    const cookieStatements = cookies
      .map(cookie => {
        const { name, value, domain, path = '/' } = cookie;
        let cookieString = `${name}=${value}; path=${path}`;
        if (domain) {
          cookieString += `; domain=${domain}`;
        }
        return `document.cookie = "${cookieString}";`;
      })
      .join('\n');

    return `
      (function() {
        ${cookieStatements}
      })();
    `;
  }, [cookies]);

  const getCombinedScript = useCallback(
    () =>
      [getCookieInjectionScript(), injectedJavaScript].filter(Boolean).join('\n'),
    [getCookieInjectionScript, injectedJavaScript],
  );

  const handleInjectedJavaScript = useCallback(
    (html: string | undefined) => {
      const combinedScript = getCombinedScript();
      if (!html) return html;

      if (!combinedScript) return html;

      const scriptTag = `<script>${combinedScript}</script>`;
      // `</body>` 가 없는 조각 HTML(CMS 콘텐츠 등)이면 replace 가 아무것도 안 해서
      // 스크립트가 통째로 누락된다. 그런 경우 끝에 덧붙인다.
      if (!html.includes('</body>')) {
        return `${html}${scriptTag}`;
      }
      return html.replace('</body>', `${scriptTag}</body>`);
    },
    [getCombinedScript],
  );

  // fetch + srcDoc 경로가 필요한 조건. iframe 의 `src` 는 GET 네비게이션이라
  // `method: 'GET'` 은 fetch 할 이유가 없다(그냥 src 로 로드하면 된다). 이걸 트리거로 두면
  // 문서화 목적으로 method 를 명시한 소비처가 조용히 srcDoc 경로로 넘어가고, 그 경로는
  // cross-origin fetch 라 CORS 에 걸린다.
  const normalizedMethod = source.method?.toUpperCase();
  const needsFetch =
    (isNotEmpty(normalizedMethod) && normalizedMethod !== 'GET') ||
    isNotEmpty(source.body) ||
    (cookies != null && cookies.length > 0) ||
    isNotEmpty(injectedJavaScript);

  const handleSourceInIFrame = useCallback(() => {
    if (!source.uri || !needsFetch) return;

    // 대상이 바뀌면 이전 결과/실패 상태를 버린다(같은 값이면 React 가 리렌더를 생략).
    setHtml(null);
    setFetchFailed(false);

    // Cookie를 헤더에 추가
    const fetchHeaders: Record<string, string> = {};
    if (cookies && cookies.length > 0) {
      const cookieHeader = cookies.map(c => `${c.name}=${c.value}`).join('; ');
      fetchHeaders['Cookie'] = cookieHeader;
    }

    fetch(source.uri, {
      method: source.method ?? 'GET',
      body: source.body,
      headers: { ...fetchHeaders, ...source.headers },
      credentials: 'include',
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.text();
      })
      .then(html => setHtml(`<base href="${source.uri}" />${html}`))
      .catch(error => {
        // cross-origin(CORS) 차단이나 네트워크 실패. 여기서 삼키면 srcDoc 이 비어
        // iframe 이 about:blank 로 남으므로 src 직접 로드로 폴백한다.
        // 단, 이 경우 injectedJavaScript/cookie 주입은 적용되지 않는다
        // (cross-origin 문서에는 주입 자체가 불가능).
        if (__DEV__) {
          console.warn(
            `[WebWebView] 콘텐츠 fetch 실패 → src 직접 로드로 폴백: ${source.uri}`,
            error,
          );
        }
        setFetchFailed(true);
      });
  }, [
    cookies,
    source.uri,
    source.method,
    source.body,
    source.headers,
    needsFetch,
  ]);

  // iframe 내부(cross-origin 웹뷰 콘텐츠)가 window.parent.postMessage 로 보낸 메시지를
  // 네이티브 webview 의 onMessage 시그니처({ nativeEvent: { data } })로 정규화해 위임한다.
  // (기존엔 raw MessageEvent 를 그대로 넘겨 소비측의 nativeEvent.data 파싱이 실패했음)
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (!onMessage) return;
      // 이 iframe 이 보낸 메시지만 신뢰(다른 위젯/광고 프레임 차단). title 리스너와 동일.
      if (event.source !== frameRef.current?.contentWindow) return;
      const data =
        typeof event.data === 'string'
          ? event.data
          : JSON.stringify(event.data);
      // source/origin 은 결과를 되돌려줘야 하는 양방향 브릿지용. 위 검증을 통과했으므로
      // source 는 이 iframe 의 contentWindow 다.
      onMessage({
        nativeEvent: { data },
        source: event.source as Window,
        origin: event.origin,
      });
    },
    [onMessage],
  );

  // [same-origin] iframe 의 document.title 변경 감시.
  // title 변경엔 전용 DOM 이벤트가 없으므로 <head> 서브트리를 MutationObserver 로 관찰한다.
  // (SPA 가 <title> 노드를 통째로 교체하는 경우까지 잡기 위해 title 요소가 아닌 head 를 관찰)
  // cross-origin 이면 contentDocument 가 null 이라 자동으로 no-op → postMessage 경로로 위임된다.
  const disconnectTitleObserver = useCallback(() => {
    titleObserverRef.current?.disconnect();
    titleObserverRef.current = null;
  }, []);

  const observeTitle = useCallback(() => {
    if (!onTitleChange) return;
    // cross-origin: contentDocument 접근이 SOP 로 차단되어 null 반환 → 관찰 불가.
    const doc = frameRef.current?.contentDocument;
    const head = doc?.head;
    if (!doc || !head) return;
    const emit = () => onTitleChange(doc.title);
    emit(); // 초기 title 반영
    const observer = new MutationObserver(emit);
    observer.observe(head, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    titleObserverRef.current = observer;
  }, [onTitleChange]);

  const handleLoad = useCallback(() => {
    // 전체 페이지 네비게이션마다 문서가 교체되므로 관찰자를 재부착한다.
    disconnectTitleObserver();
    observeTitle();
    onLoad?.();
  }, [disconnectTitleObserver, observeTitle, onLoad]);

  useEffect(() => disconnectTitleObserver, [disconnectTitleObserver]);

  // [cross-origin] contentDocument 접근이 막힌 경우, iframe 내부 페이지가 title 을
  // postMessage 로 보내주면 부모가 받아 반영한다. (프로토콜: { type: 'TITLE_CHANGED', title })
  // same-origin 이면 위 MutationObserver 가 이미 처리하므로 이 리스너는 보조 경로로만 동작한다.
  useEffect(() => {
    if (!onTitleChange) return;
    const handleTitleMessage = (event: MessageEvent) => {
      // 이 iframe 이 보낸 메시지만 신뢰(다른 위젯/광고 프레임의 메시지 차단).
      if (event.source !== frameRef.current?.contentWindow) return;
      try {
        const data =
          typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data?.type === 'TITLE_CHANGED' && typeof data.title === 'string') {
          onTitleChange(data.title);
        }
      } catch {
        // title 메시지가 아니면 무시
      }
    };
    window.addEventListener('message', handleTitleMessage);
    return () => window.removeEventListener('message', handleTitleMessage);
  }, [onTitleChange]);

  useEffect(() => {
    handleSourceInIFrame();
  }, [handleSourceInIFrame]);

  // message 이벤트 리스너
  useEffect(() => {
    if (!onMessage) return;
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onMessage, handleMessage]);

  // fetch 가 실패했으면 srcDoc 을 포기하고 src 로 직접 로드한다(빈 iframe 방지).
  const useSrcDoc = needsFetch && !fetchFailed;

  return createElement('iframe', {
    title,
    ref: frameRef,
    src: !useSrcDoc ? source.uri : undefined,
    srcDoc: useSrcDoc
      ? handleInjectedJavaScript(html ?? source.html ?? undefined)
      : undefined,
    width: width,
    height: height,
    style: StyleSheet.flatten([
      styles.iframe,
      // 스크롤을 끄고 싶을 때만 overflow:hidden 을 준다.
      // (조건이 뒤집혀 있어서 `scrollEnabled` 기본값 true 인 모든 웹뷰 화면이 내부 스크롤을
      //  못 하고 있었다 — 터치가 iframe 안에서 소비되지 않아 바깥 페이지가 대신 스크롤됨)
      !scrollEnabled && styles.noScroll,
      style,
    ]),
    allowFullScreen: true,
    allowpaymentrequest: 'true',
    frameBorder: '0',
    seamless: true,
    onLoad: handleLoad,
  });
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iframe: {
    width: '100%',
    height: '100%',
    borderWidth: 0,
  },
  noScroll: {
    overflow: 'hidden',
  },
});
